/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { openNote } from '../community/events-notes-navigator.runtime.js';
import { loadNotificationBadge } from '../groups/groups-notifications.runtime.js';
import { $, $$, DEFAULT_ACCENT_COLOR, DEFAULT_INTERFACE_MODE, DEFAULT_INTERFACE_STYLE, DEFAULT_NAVIGATION_ORDER, DEFAULT_THEME, api, authBackStage, clearAppBoot, clearPendingInvite, compactMode, cookie, escapeHTML, hideLegalPage, isProjectAdmin, loadAllGroupPages, rawRequest, revealAppFromBoot, setAuthError, setSessionHint, simpleMode, simpleParentView, state, superGrokMode, syncInviteBanner, t, titleKeys, toast, trackProduct } from '../platform/core.runtime.js';
import { closeDialog, openDialog } from '../platform/dialog-boot.runtime.js';
import { adminAccessibleGroups, completeOnboardingV13, currentMembership, ensureMFAForSession, ensurePersonalDataConsent, leaveGuestForInviteAuth, loadBase, maybeOpenProductOnboarding, renderAccount, renderGroupSelector, setAuthStage, snoozeOnboarding, updateGuestChrome } from '../session/session.runtime.js';
import { applyInterfacePreferences, focusResourceDeepLink, goToScheduleEntry, openSuperGrokOrb, saveAppearancePreferences, setKuniQuickOpen, setView, syncKuniQuick } from '../shell/navigation.runtime.js';

export /** @param {{force?:boolean,reason?:'join'|'replay'|''}} [opts] */
function openOnboardingWizardV13(opts = {}) {
  const force = Boolean(opts.force);
  const reason = opts.reason || (force ? 'replay' : '');
  if (document.body?.classList.contains('kuni-tour-active')) return;

  const hasGroup = Boolean(state.groupID && (currentMembership() || state.memberships.some((m) => m.group_id === state.groupID)));
  const groupName = currentMembership()?.group_name || state.groups.find((g) => g.id === state.groupID)?.name || 'вашей группы';

  if ($('#dialog')?.open) {
    try { closeDialog(); } catch { /* ignore */ }
  }

  const visible = (el) => {
    if (!el || el.hidden) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 2 && rect.height > 2 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  };

  const pick = (selectors = []) => {
    for (const selector of selectors) {
      if (!selector) continue;
      const list = typeof selector === 'string' ? $$(selector) : [selector];
      for (const el of list) {
        if (visible(el)) return el;
      }
    }
    return null;
  };

  const buildSteps = () => {
    const steps = [];
    steps.push({
      id: 'welcome',
      kicker: reason === 'join' ? 'Группа подключена' : 'Знакомство',
      title: reason === 'join' ? `Вы в «${groupName}»` : 'Это ваш kUni',
      body: reason === 'join'
        ? 'Подсветим реальные кнопки: где пары, задания, поиск и настройки.'
        : 'Пройдёмся по интерфейсу — подсветка на живых кнопках, без макетов.',
      selectors: [],
      pad: 0,
    });

    if (hasGroup) {
      steps.push({
        id: 'group',
        kicker: 'Группа',
        title: 'Учебная группа',
        body: `Сейчас активна «${groupName}». Расписание, ДЗ и материалы привязаны к ней. Сменить можно здесь.`,
        selectors: ['#group-selector', '.group-selector-wrap', '#guest-group-selector'],
        pad: 10,
      });
    } else {
      steps.push({
        id: 'group-missing',
        kicker: 'Группа',
        title: 'Подключите группу',
        body: 'Без группы нет расписания и заданий. Откройте «Группа» / «Ещё» и введите код приглашения.',
        selectors: [
          '.sg-dock-item[data-sg-view="morehub"]',
          '[data-simple-mobile-view="morehub"]',
          '[data-simple-view="morehub"]',
          '[data-mobile-section="more"]',
          '[data-section="more"]',
          '[data-section-view="group"]',
        ],
        pad: 10,
        prepare: async () => {
          if (compactMode()) await setView('morehub', { replace: true });
          else await setView('group', { replace: true });
        },
      });
    }

    steps.push({
      id: 'home',
      kicker: 'Навигация',
      title: 'Главная',
      body: 'Ближайшая пара, срочные дела и быстрый обзор дня.',
      selectors: [
        '.sg-dock-item[data-sg-view="home"]',
        '[data-simple-mobile-view="home"]',
        '[data-simple-view="home"]',
        '[data-mobile-section="today"]',
        '[data-section="today"]',
      ],
      pad: 8,
      prepare: async () => {
        if (compactMode()) await setView('home', { replace: true });
      },
    });

    steps.push({
      id: 'schedule',
      kicker: 'Навигация',
      title: 'Пары / расписание',
      body: 'Неделя, детали занятия и «сейчас». Свайп по неделе — если удобно.',
      selectors: [
        '.sg-dock-item[data-sg-view="schedule"]',
        '[data-simple-mobile-view="schedule"]',
        '[data-simple-view="schedule"]',
        '[data-mobile-section="today"]',
        '[data-section="today"]',
        '[data-section-view="schedule"]',
      ],
      pad: 8,
    });

    steps.push({
      id: 'study',
      kicker: 'Навигация',
      title: 'Учёба',
      body: 'Домашка, автомат, решения, конспекты и материалы — всё учебное здесь.',
      selectors: [
        '.sg-dock-item[data-sg-view="studyhub"]',
        '[data-simple-mobile-view="studyhub"]',
        '[data-simple-view="studyhub"]',
        '[data-mobile-section="study"]',
        '[data-section="study"]',
      ],
      pad: 8,
    });

    steps.push({
      id: 'more-campus',
      kicker: 'Навигация',
      title: superGrokMode() || simpleMode() ? 'Ещё' : 'Кампус и ещё',
      body: superGrokMode() || simpleMode()
        ? 'Группа, навигатор, мероприятия, уведомления и админка — в «Ещё».'
        : 'Кампус — карта и маршруты. «Ещё» — уведомления, группа и админка.',
      selectors: [
        '.sg-dock-item[data-sg-view="morehub"]',
        '[data-simple-mobile-view="morehub"]',
        '[data-simple-view="morehub"]',
        '[data-mobile-section="campus"]',
        '[data-section="campus"]',
        '[data-mobile-section="more"]',
        '[data-section="more"]',
      ],
      pad: 8,
    });

    steps.push({
      id: 'orb',
      kicker: 'Быстрые действия',
      title: 'Центральная кнопка',
      body: 'Орб ✦ — быстрый переход: добавить пару, ДЗ, открыть навигатор и другие действия.',
      selectors: ['#sg-orb', '.sg-orb'],
      pad: 14,
      shape: 'circle',
    });

    const openQuick = async () => {
      if (typeof setKuniQuickOpen === 'function') setKuniQuickOpen(true);
      await new Promise((r) => setTimeout(r, 220));
    };

    steps.push({
      id: 'search',
      kicker: 'Плавающая кнопка',
      title: 'Поиск',
      body: 'Три точки открывают меню: сама кнопка становится поиском. На компьютере — Ctrl/⌘+K.',
      selectors: ['#global-search-button'],
      pad: 10,
      shape: 'circle',
      prepare: openQuick,
    });

    steps.push({
      id: 'telegram',
      kicker: 'Плавающая кнопка',
      title: 'Telegram',
      body: 'Кружок сверху — бот для PDF, напоминаний и быстрых действий вне браузера.',
      selectors: ['#telegram-shortcut'],
      pad: 10,
      shape: 'circle',
      prepare: openQuick,
    });

    steps.push({
      id: 'personalize',
      kicker: 'Шапка',
      title: 'Оформление',
      body: 'Режим интерфейса, стиль и акцентный цвет. Можно менять в любой момент.',
      selectors: ['#personalization-button', '[data-open-settings]', '#mobile-settings-tab', '.global-settings-item'],
      pad: 10,
    });

    steps.push({
      id: 'help',
      kicker: 'Напоследок',
      title: 'Повторить тур',
      body: 'Три точки → кружок с вопросом. Этот гайд можно открыть снова в любой момент.',
      selectors: ['#help-onboarding-button'],
      pad: 10,
      shape: 'circle',
      prepare: openQuick,
    });

    steps.push({
      id: 'done',
      kicker: 'Готово',
      title: reason === 'join' ? 'Можно учиться' : 'Тур завершён',
      body: 'Интерфейс тот же — теперь вы знаете, за что отвечает каждая кнопка.',
      selectors: [],
      pad: 0,
    });

    return steps;
  };

  const run = async () => {
    try {
      ensureSuperGrokChrome?.();
      if (compactMode() && !['home', 'schedule', 'studyhub', 'morehub'].includes(state.view)) {
        await setView('home', { replace: true });
      }
    } catch { /* chrome may not exist yet */ }

    const steps = buildSteps();
    let index = 0;
    let targetEl = null;
    let resizeObserver = null;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish({ snooze: !force, complete: false });
      } else if (event.key === 'ArrowRight' || event.key === 'Enter') {
        event.preventDefault();
        go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      }
    };
    const onResize = () => layout();

    const root = document.createElement('div');
    root.className = 'kuni-tour-root';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Интерактивный тур по kUni');
    root.innerHTML = `
      <div class="kuni-tour-pad" data-tour-pad="t"></div>
      <div class="kuni-tour-pad" data-tour-pad="l"></div>
      <div class="kuni-tour-pad" data-tour-pad="r"></div>
      <div class="kuni-tour-pad" data-tour-pad="b"></div>
      <div class="kuni-tour-hole" data-tour-hole hidden></div>
      <div class="kuni-tour-card" data-tour-card>
        <div class="kuni-tour-progress" data-tour-progress aria-hidden="true"></div>
        <span class="kuni-tour-kicker" data-tour-kicker></span>
        <h3 class="kuni-tour-title" data-tour-title></h3>
        <p class="kuni-tour-body" data-tour-body></p>
        <div class="kuni-tour-actions">
          <button type="button" class="button ghost small" data-tour-skip>Пропустить</button>
          <div class="kuni-tour-nav">
            <button type="button" class="button small" data-tour-back>Назад</button>
            <button type="button" class="button primary small" data-tour-next>Дальше</button>
          </div>
        </div>
      </div>`;
    document.body.append(root);
    document.body.classList.add('kuni-tour-active');
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    const hole = root.querySelector('[data-tour-hole]');
    const card = root.querySelector('[data-tour-card]');
    const padT = root.querySelector('[data-tour-pad="t"]');
    const padL = root.querySelector('[data-tour-pad="l"]');
    const padR = root.querySelector('[data-tour-pad="r"]');
    const padB = root.querySelector('[data-tour-pad="b"]');

    root.querySelector('[data-tour-skip]')?.addEventListener('click', () => finish({ snooze: false, complete: true }));
    root.querySelector('[data-tour-back]')?.addEventListener('click', () => go(-1));
    root.querySelector('[data-tour-next]')?.addEventListener('click', () => go(1));

    const clearTarget = () => {
      if (targetEl) {
        targetEl.classList.remove('kuni-tour-target');
        targetEl.removeAttribute('data-kuni-tour-target');
        targetEl = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    const setPads = (rect, pad = 8) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!rect) {
        padT.style.cssText = `top:0;left:0;width:100%;height:100%`;
        padL.style.cssText = 'display:none';
        padR.style.cssText = 'display:none';
        padB.style.cssText = 'display:none';
        hole.hidden = true;
        return;
      }
      const x = Math.max(0, rect.left - pad);
      const y = Math.max(0, rect.top - pad);
      const w = Math.min(vw - x, rect.width + pad * 2);
      const h = Math.min(vh - y, rect.height + pad * 2);
      const right = x + w;
      const bottom = y + h;
      padT.style.cssText = `display:block;top:0;left:0;width:100%;height:${y}px`;
      padL.style.cssText = `display:block;top:${y}px;left:0;width:${x}px;height:${h}px`;
      padR.style.cssText = `display:block;top:${y}px;left:${right}px;width:${Math.max(0, vw - right)}px;height:${h}px`;
      padB.style.cssText = `display:block;top:${bottom}px;left:0;width:100%;height:${Math.max(0, vh - bottom)}px`;
      hole.hidden = false;
      const radius = steps[index]?.shape === 'circle' ? '999px' : '14px';
      hole.style.cssText = `top:${y}px;left:${x}px;width:${w}px;height:${h}px;border-radius:${radius}`;
    };

    const placeCard = (rect) => {
      const margin = 12;
      const cw = card.offsetWidth || 300;
      const ch = card.offsetHeight || 180;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top;
      let left;
      if (!rect) {
        top = Math.max(margin, (vh - ch) / 2);
        left = Math.max(margin, (vw - cw) / 2);
      } else {
        const below = rect.bottom + 14;
        const above = rect.top - ch - 14;
        if (below + ch + margin <= vh) top = below;
        else if (above >= margin) top = above;
        else top = Math.min(vh - ch - margin, Math.max(margin, rect.top));
        left = rect.left + rect.width / 2 - cw / 2;
        left = Math.min(vw - cw - margin, Math.max(margin, left));
      }
      card.style.top = `${Math.round(top)}px`;
      card.style.left = `${Math.round(left)}px`;
    };

    const layout = () => {
      const step = steps[index];
      if (!step) return;
      const el = targetEl && visible(targetEl) ? targetEl : null;
      const rect = el ? el.getBoundingClientRect() : null;
      setPads(rect, step.pad ?? 8);
      // measure card after content paint
      requestAnimationFrame(() => placeCard(rect));
    };

    const paint = async () => {
      const step = steps[index];
      if (!step) return;
      clearTarget();
      if (typeof step.prepare === 'function') {
        try { await step.prepare(); } catch { /* ignore navigate errors */ }
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      }
      const el = pick(step.selectors || []);
      targetEl = el;
      if (el) {
        el.classList.add('kuni-tour-target');
        el.setAttribute('data-kuni-tour-target', '1');
        try {
          el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        } catch { /* ignore */ }
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => layout());
          resizeObserver.observe(el);
        }
      }

      root.querySelector('[data-tour-kicker]').textContent = step.kicker || `Шаг ${index + 1}`;
      root.querySelector('[data-tour-title]').textContent = step.title || '';
      root.querySelector('[data-tour-body]').textContent = step.body || '';
      const progress = root.querySelector('[data-tour-progress]');
      progress.innerHTML = steps.map((_, i) => `<i class="${i < index ? 'done' : ''}${i === index ? ' active' : ''}"></i>`).join('');
      const back = root.querySelector('[data-tour-back]');
      const next = root.querySelector('[data-tour-next]');
      if (back) {
        back.disabled = index === 0;
        back.hidden = index === 0;
      }
      if (next) next.textContent = index >= steps.length - 1 ? 'Готово' : 'Дальше';
      layout();
      next?.focus({ preventScroll: true });
    };

    const go = (delta) => {
      const nextIndex = index + delta;
      if (nextIndex < 0) return;
      if (nextIndex >= steps.length) {
        finish({ snooze: false, complete: true });
        return;
      }
      index = nextIndex;
      paint();
    };

    const finish = async ({ snooze = false, complete = false } = {}) => {
      if (typeof setKuniQuickOpen === 'function') setKuniQuickOpen(false);
      clearTarget();
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      root.remove();
      document.body.classList.remove('kuni-tour-active');
      if (complete) {
        await completeOnboardingV13();
        if (typeof setKuniQuickOpen === 'function') setKuniQuickOpen(false);
        toast(reason === 'join' ? 'Группа подключена — можно пользоваться' : 'Тур завершён · повтор: три точки → помощь');
      } else if (snooze) {
        snoozeOnboarding(24);
      }
    };

    await paint();
  };

  run().catch((error) => {
    console.error('onboarding tour failed', error);
    toast(error?.message || 'Не удалось открыть тур');
  });
}

async function alignGroupForDeepLink(link) {
  const endpoint = ({
    lesson: `/schedule/${link?.id || ''}`,
    note: `/notes/${link?.id || ''}`,
    homework: `/homework/${link?.id || ''}`,
    requirement: `/requirements/${link?.id || ''}`,
    material: `/materials/${link?.id || ''}`,
  })[link?.type];
  if (!endpoint || !link?.id) return null;
  try {
    const item = await api(endpoint);
    const groupID = item?.group_id;
    if (groupID && groupID !== state.groupID) {
      const allowed = adminAccessibleGroups().some((group)=>group.id===groupID) || state.memberships.some((membership) => membership.group_id === groupID && membership.status === 'verified');
      if (allowed) {
        state.groupID = groupID;
        localStorage.setItem('kuni-group-id', groupID);
        renderGroupSelector();
      }
    }
    return item;
  } catch (error) {
    return { __deepLinkError: error };
  }
}

export async function enterApp() {
  state.isGuest = false;
  hideLegalPage();
  setSessionHint(true);
  // Auth stays hidden under boot overlay until app is ready to paint.
  revealAppFromBoot();
  updateGuestChrome();
  renderAccount();
  try {
    await ensureMFAForSession();
    const legal = await ensurePersonalDataConsent();
    if (!legal) return;
    await loadBase();
    trackProduct('app.opened', { deep_link: state.deepLink?.type || 'none' });
    const invitation = await applyPendingInvite();
    const link = state.deepLink?.type === 'join' ? {type:'none'} : state.deepLink;
    const resolvedDeepLink = await alignGroupForDeepLink(link);
    const requestParams = new URLSearchParams(window.location.search);
    const requestedView = requestParams.get('view');
    if (invitation?.membership) state.view = 'group';
    else if (link?.type === 'course') { state.courseSubjectID = link.id; state.view = 'course'; }
    else if (link?.type === 'homework') state.view = 'homework';
    else if (link?.type === 'requirement') state.view = 'requirements';
    else if (link?.type === 'material') state.view = 'materials';
    else if (link?.type === 'note') state.view = 'notes';
    else if (link?.type === 'lesson') state.view = 'schedule';
    else if (link?.type === 'view' && link.view === 'ui_text_editor') {
      if (!isProjectAdmin()) {
        history.replaceState(null, '', compactMode() ? '/main' : '/schedule');
        state.view = compactMode() ? 'home' : 'schedule';
        toast(t('editor.admin_only'));
      } else {
        state.view = 'ui_text_editor';
      }
    }
    else if (link?.type === 'view' && Object.prototype.hasOwnProperty.call(titleKeys, link.view)) state.view = link.view;
    else if (requestedView && Object.prototype.hasOwnProperty.call(titleKeys, requestedView)) state.view = requestedView;
    else if (compactMode()) state.view = 'home';
    await setView(state.view, { replace: true });
    if (link?.type === 'lesson') goToScheduleEntry(link.id).catch((error)=>toast(error.message));
    else if (link?.type === 'note') openNote(link.id).catch((error)=>toast(error.message));
    else if (['homework','requirement','material'].includes(link?.type)) focusResourceDeepLink(link.id);
    if (invitation?.membership) showInviteResult(invitation.membership);
    else if (invitation?.error) openDialog('Приглашение не сработало', `<div class="invite-result error"><strong>Не удалось вступить в группу</strong><p>${escapeHTML(invitation.error.message)}</p></div>`, [{label:'Закрыть',action:closeDialog}]);
    else if (resolvedDeepLink?.__deepLinkError) toast('Ссылка ведёт к недоступному или удалённому объекту');
    loadNotificationBadge();
    // First login / join: product tour. Replay available via lifebuoy in the header.
    if (invitation?.membership?.status === 'verified') {
      // Invite success dialog first; tour starts from its primary action.
    } else if (!invitation && (!link || link.type === 'none')) {
      setTimeout(() => maybeOpenProductOnboarding().catch(() => {}), 450);
    }
  } catch (error) {
    console.error(error);
    toast(error.message || t('common.error.open'));
  }
}

async function applyPendingInvite() {
  const code = state.pendingInviteCode;
  if (!code || state.isGuest) return null;
  try {
    const membership = await api('/groups/join', { method:'POST', body:JSON.stringify({code}) });
    trackProduct('group.joined', { status: membership.status });
    clearPendingInvite();
    await loadBase();
    if (membership.status === 'verified') {
      state.groupID = membership.group_id;
      localStorage.setItem('kuni-group-id', membership.group_id);
      renderGroupSelector();
    }
    return { membership };
  } catch (error) {
    clearPendingInvite();
    return { error };
  }
}

export function showInviteResult(membership) {
  const verified = membership.status === 'verified';
  openDialog(
    verified ? 'Вы в группе' : 'Заявка отправлена',
    `<div class="invite-result ${verified ? 'success' : 'pending'}"><span class="invite-result-icon" aria-hidden="true">${verified ? '✓' : '⌛'}</span><strong>${escapeHTML(membership.group_name)}</strong><p>${verified ? 'Доступ к материалам группы уже открыт. Дальше — короткий тур: где пары, задания и кампус.' : 'Администратор группы увидит заявку и подтвердит вступление.'}</p></div>`,
    [{
      label: verified ? 'Как пользоваться' : 'Понятно',
      primary: true,
      action: () => {
        closeDialog();
        if (verified) setTimeout(() => openOnboardingWizardV13({ reason: 'join' }), 120);
      },
    }],
  );
}

export async function enterGuest(selectedGroup = null) {
  setAuthError();
  const button = $('#guest-login');
  if (button) button.disabled = true;
  const inviteGuestBtn = $('#invite-guest-enter');
  if (inviteGuestBtn) inviteGuestBtn.disabled = true;
  try {
    state.isGuest = true;
    hideLegalPage();
    state.accessToken = '';
    state.user = { id: 'guest', display_name: 'Гость', email: '' };
    state.roles = [];
    state.memberships = [];
    state.view = 'schedule';
    state.weekOffset = 0;
    state.publicTeacher = null;
    const explicit = selectedGroup && selectedGroup.id ? selectedGroup : null;
    // Prefer the invited group without waiting for full public catalog when we already have it.
    if (explicit) {
      state.groups = [explicit];
      state.groupID = explicit.id;
      localStorage.setItem('kuni-guest-group-id', explicit.id);
      // Warm the catalog in the background so the group selector has options.
      loadAllGroupPages('/public/groups', false).then((groups) => {
        if (!state.isGuest) return;
        const items = groups?.items || groups || [];
        if (!items.length) return;
        if (!items.some((group) => group.id === explicit.id)) state.groups = [explicit, ...items];
        else state.groups = items;
        renderGroupSelector();
      }).catch(() => {});
    } else {
      const groups = await loadAllGroupPages('/public/groups', false);
      state.groups = groups?.items || groups || [];
      const saved = localStorage.getItem('kuni-guest-group-id');
      state.groupID = state.groups.some((group) => group.id === saved) ? saved : (state.groups[0]?.id || '');
    }
    applyInterfacePreferences({ navigation_order: [...DEFAULT_NAVIGATION_ORDER], accent_color: DEFAULT_ACCENT_COLOR, interface_mode: 'context', interface_style: DEFAULT_INTERFACE_STYLE, theme: DEFAULT_THEME });
    renderGroupSelector();
    // App first, then drop overlay — never clear boot while auth is still visible.
    revealAppFromBoot();
    updateGuestChrome();
    renderAccount();
    await setView('schedule');
  } catch (error) {
    leaveApp();
    setAuthError(error.message || 'Не удалось открыть гостевой режим');
    throw error;
  } finally {
    if (button) button.disabled = false;
    if (inviteGuestBtn) inviteGuestBtn.disabled = false;
  }
}

export async function enterTeacherGuest(teacher) {
  if (!teacher?.id) return;
  setAuthError(); hideLegalPage(); state.isGuest=true; state.accessToken=''; state.user={id:'guest-teacher',display_name:'Гость',email:''}; state.roles=[];state.memberships=[];state.groups=[];state.groupID='';state.publicTeacher=teacher;state.view='schedule';state.weekOffset=0;
  applyInterfacePreferences({navigation_order:[...DEFAULT_NAVIGATION_ORDER],accent_color:DEFAULT_ACCENT_COLOR,interface_mode:'context',interface_style:DEFAULT_INTERFACE_STYLE,theme:DEFAULT_THEME});
  renderGroupSelector(); revealAppFromBoot(); updateGuestChrome();renderAccount();await setView('schedule');
}

export function leaveApp() {
  state.accessToken = ''; state.user = null; state.isGuest = false; state.roles = []; state.memberships = []; state.groups = []; state.groupID = ''; state.publicTeacher = null; state.view = 'schedule'; state.weekOffset = 0;
  setSessionHint(false);
  applyInterfacePreferences({ navigation_order: [...DEFAULT_NAVIGATION_ORDER], accent_color: DEFAULT_ACCENT_COLOR, interface_mode: DEFAULT_INTERFACE_MODE, interface_style: DEFAULT_INTERFACE_STYLE, theme: DEFAULT_THEME });
  updateGuestChrome();
  hideLegalPage();
  $('#app-view').hidden = true;
  document.documentElement.classList.add('kuni-auth-ready');
  $('#auth-view').hidden = false;
  clearAppBoot();
  setAuthStage(authBackStage());
  syncSuperGrokChrome();
  if (typeof syncKuniQuick === 'function') syncKuniQuick();
  syncInviteBanner();
}

var systemThemeMedia = window.matchMedia('(prefers-color-scheme: dark)');

function normalizeThemePreference(value) {
  return value === 'light' || value === 'system' || value === 'dark' ? value : 'dark';
}

export function currentThemePreference() {
  return normalizeThemePreference(state.interfacePreferences?.theme || localStorage.getItem('kuni-theme') || DEFAULT_THEME);
}

export function resolveTheme(preference) {
  return preference === 'system' ? (systemThemeMedia.matches ? 'dark' : 'light') : preference;
}

export function applyTheme(theme) {
  const resolved = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.dataset.theme = resolved;
  const body = document.body;
  const chrome = body?.classList.contains('style-khokhloma')
    ? (resolved === 'dark' ? '#0D0A07' : '#F3E0B0')
    : body?.classList.contains('style-rzd')
      ? (resolved === 'dark' ? '#121214' : '#E6E7EA')
      : body?.classList.contains('style-gzhel')
        ? (resolved === 'dark' ? '#071A3A' : '#EEF3F9')
        : (resolved === 'dark' ? '#0e1013' : '#f7f7f4');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', chrome);
  applyAccentColor(state.interfacePreferences.accent_color);
}

export function setThemePreference(preference, persist = true) {
  const normalized = normalizeThemePreference(preference);
  if (persist) localStorage.setItem('kuni-theme', normalized);
  applyTheme(resolveTheme(normalized));
  document.body.dataset.themePreference = normalized;
  return normalized;
}

function normalizeAccentColor(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : DEFAULT_ACCENT_COLOR;
}

export function normalizeNavigationOrder(value) {
  if (!Array.isArray(value) || value.length !== DEFAULT_NAVIGATION_ORDER.length) return [...DEFAULT_NAVIGATION_ORDER];
  const unique = new Set(value);
  return unique.size === DEFAULT_NAVIGATION_ORDER.length && DEFAULT_NAVIGATION_ORDER.every((item) => unique.has(item)) ? [...value] : [...DEFAULT_NAVIGATION_ORDER];
}

export function normalizeInterfaceMode(value) {
  return value === 'simple' || value === 'classic' || value === 'context' || value === 'supergrok' ? value : DEFAULT_INTERFACE_MODE;
}

export function normalizeInterfaceStyle(value) {
  return value === 'bento' || value === 'night' || value === 'kawaii' || value === 'tor' || value === 'khokhloma' || value === 'rzd' || value === 'gzhel' || value === 'default' ? value : DEFAULT_INTERFACE_STYLE;
}

export function isRussianInterfaceStyle(value) {
  const style = normalizeInterfaceStyle(value);
  return style === 'khokhloma' || style === 'rzd' || style === 'gzhel';
}

function normalizeChronicleInitials(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function normalizeInterfacePreferences(value = {}) {
  // 12.3.0 briefly stored Bento/Night as interface_mode. Keep local-cache upgrades seamless.
  const legacyStyle = value.interface_mode === 'bento' || value.interface_mode === 'night' ? value.interface_mode : '';
  const rawMode = legacyStyle ? DEFAULT_INTERFACE_MODE : value.interface_mode;
  return {
    navigation_order: normalizeNavigationOrder(value.navigation_order),
    accent_color: normalizeAccentColor(value.accent_color),
    interface_mode: normalizeInterfaceMode(rawMode),
    interface_style: normalizeInterfaceStyle(value.interface_style || legacyStyle),
    theme: normalizeThemePreference(value.theme),
    chronicle_initials: normalizeChronicleInitials(value.chronicle_initials),
    updated_at: value.updated_at || null,
  };
}

function interfacePreferenceStorageKey() { return `kuni-interface-${state.user?.id || 'anonymous'}`; }

export function loadLocalInterfacePreferences() {
  try { return JSON.parse(localStorage.getItem(interfacePreferenceStorageKey()) || 'null'); }
  catch { return null; }
}

export function saveLocalInterfacePreferences(preferences) {
  try { localStorage.setItem(interfacePreferenceStorageKey(), JSON.stringify(preferences)); }
  catch { /* browser storage is only a fallback */ }
}

function mixHex(hex, target, amount) {
  const source = hex.match(/[0-9A-F]{2}/gi).map((part) => parseInt(part, 16));
  const mixed = source.map((channel) => Math.round(channel + (target - channel) * amount));
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

var accentCSSRule = null;

function getAccentCSSRule() {
  if (accentCSSRule) return accentCSSRule;
  for (const sheet of document.styleSheets) {
    let rules;
    try { rules = sheet.cssRules; } catch { continue; }
    accentCSSRule = [...rules].find((rule) => rule.selectorText === ':root[data-user-accent]') || null;
    if (accentCSSRule) return accentCSSRule;
  }
  return null;
}

export function applyAccentColor(value) {
  const accent = normalizeAccentColor(value);
  const channels = accent.match(/[0-9A-F]{2}/gi).map((part) => parseInt(part, 16));
  const dark = document.documentElement.dataset.theme === 'dark';
  const rule = getAccentCSSRule();
  if (!rule) return;
  rule.style.setProperty('--accent', accent);
  rule.style.setProperty('--accent-hover', mixHex(accent, dark ? 255 : 0, .18));
  rule.style.setProperty('--accent-soft', `rgba(${channels.join(',')},${dark ? .18 : .10})`);
  document.documentElement.dataset.userAccent = 'true';
}

export function applyNavigationOrder(order) {
  const normalized = normalizeNavigationOrder(order);
  const desktop = $('.nav-sections');
  const mobile = $('.mobile-nav');
  normalized.forEach((sectionID, index) => {
    const desktopButton = $(`.section-nav-item[data-section="${sectionID}"]`);
    const mobileButton = $(`[data-mobile-section="${sectionID}"]`);
    if (desktopButton) { desktop.append(desktopButton); desktopButton.setAttribute('aria-posinset', String(index + 1)); }
    if (mobileButton) { mobile.append(mobileButton); mobileButton.setAttribute('aria-posinset', String(index + 1)); }
  });
  const mobileSettings = $('#mobile-settings-tab');
  if (mobileSettings && mobile) mobile.append(mobileSettings);
}

export function ensureSuperGrokChrome() {
  if ($('#sg-dock')) return;
  const dock = document.createElement('nav');
  dock.id = 'sg-dock';
  dock.className = 'sg-dock';
  dock.setAttribute('aria-label', 'Навигация');
  dock.innerHTML = `
    <button type="button" class="sg-dock-item" data-sg-view="home" aria-label="Главная"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11.5 12 4l9 7.5M5.5 10v10h13V10M9 20v-6h6v6"/></svg><span>Главная</span></button>
    <button type="button" class="sg-dock-item" data-sg-view="schedule" aria-label="Расписание"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1Z"/></svg><span>Пары</span></button>
    <button type="button" class="sg-orb" id="sg-orb" aria-label="Быстрые действия"><span class="sg-orb-core">+</span></button>
    <button type="button" class="sg-dock-item" data-sg-view="studyhub" aria-label="Учёба"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v14H4zM8 9h8M8 13h5"/></svg><span>Учёба</span></button>
    <button type="button" class="sg-dock-item" data-sg-view="morehub" aria-label="Ещё"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h.01M12 12h.01M19 12h.01"/></svg><span>Ещё</span></button>`;
  document.body.append(dock);
  $$('[data-sg-view]', dock).forEach((button) => button.addEventListener('click', () => setView(button.dataset.sgView)));
  $('#sg-orb', dock)?.addEventListener('click', () => openSuperGrokOrb().catch((e) => toast(e.message)));
}

export function syncSuperGrokChrome() {
  const dock = $('#sg-dock');
  if (!dock) return;
  const on = superGrokMode() && !$('#app-view')?.hidden;
  dock.hidden = !on;
  if (typeof syncKuniQuick === 'function') syncKuniQuick();
  if (!on) return;
  const parent = simpleParentView(state.view);
  $$('[data-sg-view]', dock).forEach((button) => button.classList.toggle('active', button.dataset.sgView === parent));
}

export /** SuperGrok glass environment — menus + nested dialogs stay on one frosted stage. */
var glassFlowActive = false;

export var glassFlowHold = false;

export function setGlassFlowHold(value) { glassFlowHold = Boolean(value); return glassFlowHold; }

export /** Last pressed control — next glass content flies out from here (viewport coords). */
var glassClickOrigin = null;

export function setGlassFlow(on) { glassFlowActive = Boolean(on); }

export function isGlassFlow() { return glassFlowActive || Boolean($('#dialog')?.classList.contains('sg-orb-dialog')); }

export function captureGlassOrigin(el) {
  if (!el?.getBoundingClientRect) {
    glassClickOrigin = null;
    return null;
  }
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) {
    glassClickOrigin = null;
    return null;
  }
  glassClickOrigin = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  return glassClickOrigin;
}

export /**
 * Rows/head fly from pressed control; footer always rises from bottom (never from top).
 */
function applyGlassOriginFromClick(root = $('#dialog')) {
  if (!root) return;
  const { x: ox, y: oy } = glassClickOrigin || { x: window.innerWidth / 2, y: window.innerHeight * 0.72 };
  // Only menu rows + sheet head + form header/body — never footer/Cancel.
  const nodes = [
    ...$$('.sg-orb-action', root),
    ...$$('.sg-sheet-head', root),
  ];
  const formHeader = root.querySelector('header:not([hidden])');
  const formBody = root.querySelector('.dialog-body');
  if (!nodes.length) {
    if (formHeader) nodes.push(formHeader);
    if (formBody) nodes.push(formBody);
  }
  nodes.forEach((el, i) => {
    if (el.closest('footer') || el.matches?.('footer')) return;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    el.style.setProperty('--sg-dx', `${(ox - cx).toFixed(1)}px`);
    el.style.setProperty('--sg-dy', `${(oy - cy).toFixed(1)}px`);
    if (!el.style.getPropertyValue('--sg-i')) el.style.setProperty('--sg-i', String(i));
    el.classList.add('sg-from-origin');
    el.classList.remove('sg-from-bottom');
  });
  // Footer always from bottom
  const footer = root.querySelector('footer');
  if (footer && !footer.hidden) {
    footer.classList.remove('sg-from-origin');
    footer.classList.add('sg-from-bottom');
    footer.style.removeProperty('--sg-dx');
    footer.style.removeProperty('--sg-dy');
  }
}

export function scheduleGlassOriginApply(root) {
  // Android WebView: keep origin polish optional; force visible solid state.
  requestAnimationFrame(() => {
    applyGlassOriginFromClick(root);
    if (!root) return;
    $$('.sg-orb-action, .sg-sheet-head', root).forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  });
}

export function initOnboardingRuntime() {
  $('#logout').addEventListener('click', async () => {
    if (!state.isGuest) {
      try {
        const csrf = decodeURIComponent(cookie('kuni_csrf') || '');
        await rawRequest('/auth/logout', { method: 'POST', headers: { 'X-CSRF-Token': csrf }, body: '{}' });
      } catch { /* local logout still applies */ }
    }
    leaveApp();
  });

  $('#guest-login')?.addEventListener('click', () => {
    setAuthStage('guest');
  });

  $('#guest-open-catalog')?.addEventListener('click', () => {
    enterGuest().catch((error) => setAuthError(error.message));
  });

  $('#guest-exit-shortcut').addEventListener('click', () => { leaveApp(); setTimeout(()=>{ setAuthStage(state.pendingInviteCode ? 'invite' : 'guest'); if (!state.pendingInviteCode) $('#auth-group-search-input')?.focus(); }, 0); });

  $('#guest-invite-register')?.addEventListener('click', () => leaveGuestForInviteAuth('register'));

  $('#guest-invite-login')?.addEventListener('click', () => leaveGuestForInviteAuth('login'));

  $('#theme-toggle').addEventListener('click', async () => {
    const next = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
    if (state.isGuest || !state.accessToken) { setThemePreference(next); return; }
    try { await saveAppearancePreferences({ theme: next }, `Тема: ${next === 'dark' ? 'тёмная' : 'светлая'}`); }
    catch { /* saveAppearancePreferences already restored the previous state */ }
  });

  systemThemeMedia.addEventListener?.('change', () => {
    if (currentThemePreference() === 'system') setThemePreference('system', false);
  });

  setThemePreference(currentThemePreference(), false);
}
